/**
 * The video an item's preview is made from: the field of the item's card (Νέο θέμα, edit) where a file is dropped or
 * chosen, read at once (what it is, how long, who will make the preview: this browser or the server); and the tray
 * where the previews being made are followed (made here, sent, made on the server), cancelled, opened when ready.
 */
import { useRef, useState, type DragEvent } from 'react';
import { formatBytes } from '@airpost/shared';
import { cancelPreview, dismissPreview, isRunning, lookAt, usePreviewJobs, type FileLook, type PreviewJob } from '../archive-preview.js';
import { t } from '../i18n.js';
import { lastSendRate, sendTimeText } from '../send-time.js';
import { hhmmss } from '../util.js';

/** A file picked for an item's preview; `look` is null while the browser reads it. */
export interface PickedVideo {
  file: File;
  look: FileLook | null;
}

/** What the server can do with a file the browser cannot read (from the archive's summary). */
export interface ServerIngest {
  server: boolean;
  maxBytes: number;
}

export const VIDEO_ACCEPT = 'video/*,.mxf,.mov,.mp4,.m4v,.mkv,.webm,.ts,.mts,.m2ts,.avi,.mpg,.mpeg,.dv';

/** Why a picked file cannot be made into a preview here, or null when it can. */
export function pickProblem(p: PickedVideo, ingest: ServerIngest | null): string | null {
  if (!p.look) return null;
  if (p.look.way === 'none') return t('ltoPickNoPicture');
  if (p.look.way === 'server' && !ingest?.server) return t('ltoPickNoServer');
  if (p.look.way === 'server' && ingest && p.file.size > ingest.maxBytes) return `${t('ltoPickTooBig')} (${formatBytes(ingest.maxBytes)})`;
  return null;
}
export const pickReady = (p: PickedVideo | null, ingest: ServerIngest | null): p is PickedVideo & { look: FileLook } => !!p?.look && !pickProblem(p, ingest);

/** A file dropped on an element, or null. */
export const droppedFile = (e: DragEvent): File | null => (e.dataTransfer.files.length ? e.dataTransfer.files[0]! : null);

/** Read a picked file; `then` hears what the browser saw (the picker shows "reading" until then). */
export function readPicked(file: File, then: (p: PickedVideo) => void): void {
  void lookAt(file).then((look) => then({ file, look }));
}

const mediaOf = (l: FileLook | null): string =>
  l ? [l.durationMs ? hhmmss(l.durationMs / 1000) : null, l.width && l.height ? `${l.width}×${l.height}` : null, l.fps ? `${l.fps} fps` : null, l.codec].filter(Boolean).join(' · ') : '';

/** The item card's "Βίντεο" row: drop or choose a file; what it is and who makes its preview. */
export function PreviewPicker({ picked, onPick, ingest, hasPreview, disabled }: { picked: PickedVideo | null; onPick: (p: PickedVideo | null) => void; ingest: ServerIngest | null; hasPreview: boolean; disabled: boolean }) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  const latest = useRef<File | null>(null);
  const take = (file: File | null) => {
    if (!file || disabled) return;
    latest.current = file;
    onPick({ file, look: null });
    // a file picked after this one wins
    readPicked(file, (p) => latest.current === file && onPick(p));
  };
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    take(droppedFile(e));
  };
  const problem = picked ? pickProblem(picked, ingest) : null;
  const way = picked?.look?.way;
  return (
    <>
      <label htmlFor="fvideo">{t('ltoPickLabel')} :</label>
      <div className="wide lto-pick">
        <input
          id="fvideo"
          ref={input}
          type="file"
          accept={VIDEO_ACCEPT}
          hidden
          onChange={(e) => {
            take(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
        {!picked ? (
          <div
            className={`lto-pick-zone${over ? ' over' : ''}${disabled ? ' off' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={drop}
          >
            <span className="lto-pick-ico" aria-hidden="true">
              ⬆
            </span>
            <div className="lto-pick-text">
              <span>
                <b>{t('ltoPickDrop')}</b> {t('ltoPickOr')}{' '}
                <button type="button" className="lto-pick-btn" disabled={disabled} onClick={() => input.current?.click()}>
                  {t('ltoPickChoose')}
                </button>
              </span>
              <small>{hasPreview ? t('ltoPickReplace') : t('ltoPickHint')}</small>
            </div>
          </div>
        ) : (
          <div className={`lto-pick-file${problem ? ' bad' : ''}`} onDragOver={(e) => e.preventDefault()} onDrop={drop}>
            <span className="lto-pick-ico film" aria-hidden="true">
              🎞
            </span>
            <div className="lto-pick-text">
              <b className="lto-pick-name" title={picked.file.name}>
                {picked.file.name}
              </b>
              <small>
                {formatBytes(picked.file.size)}
                {picked.look && mediaOf(picked.look) ? ` · ${mediaOf(picked.look)}` : ''}
              </small>
              <small className={`lto-pick-way ${problem ? 'bad' : (way ?? 'reading')}`}>
                {problem ?? (!picked.look ? t('ltoPickLooking') : way === 'browser' ? t('ltoPickBrowser') : `${t('ltoPickServer')}: ${sendTimeText(picked.file.size, lastSendRate())}`)}
              </small>
              {!problem && way === 'server' && <small>{t('ltoPickServerWhy')}</small>}
            </div>
            <button type="button" className="lto-pick-btn" onClick={() => input.current?.click()} disabled={disabled}>
              {t('ltoPickOther')}
            </button>
            <button type="button" className="lto-pick-x" aria-label={t('ltoPickRemove')} title={t('ltoPickRemove')} onClick={() => onPick(null)}>
              ✕
            </button>
          </div>
        )}
      </div>
    </>
  );
}

const stageText = (j: PreviewJob): string => {
  if (j.stage === 'making') return t('ltoJobMaking');
  if (j.stage === 'sending') return j.way === 'browser' ? t('ltoJobSendingPreview') : t('ltoJobSending');
  if (j.stage === 'server') return j.serverStage === 'queued' ? t('ltoJobQueued') : j.serverStage === 'probing' ? t('ltoJobProbing') : j.serverStage === 'storing' ? t('ltoJobStoring') : t('ltoJobServer');
  return j.stage === 'done' ? t('ltoJobDone') : j.stage === 'failed' ? t('ltoJobFailed') : t('ltoJobCancelled');
};

/** How far a preview being made is: what is being done, the bar, the rate and time left while the file is sent. */
export function JobProgress({ job: j }: { job: PreviewJob }) {
  return (
    <>
      {isRunning(j) && (
        <div className="lto-progress">
          <span style={{ width: `${Math.max(2, Math.round(j.progress * 100))}%` }} />
        </div>
      )}
      <div className="lto-job-note">
        <span>{stageText(j)}</span>
        {isRunning(j) && <span>{Math.round(j.progress * 100)}%</span>}
        {j.stage === 'sending' && j.rate ? (
          <span className="muted">
            {formatBytes(j.rate)}/s{j.etaS !== null ? ` · ${hhmmss(j.etaS)} ${t('ltoJobLeft')}` : ''}
          </span>
        ) : null}
      </div>
    </>
  );
}

/** The previews being made, in a corner of the archive: how far each is, cancel, open the item once it is ready. */
export function PreviewTray({ onOpen }: { onOpen: (videoId: string) => void }) {
  const jobs = usePreviewJobs();
  if (!jobs.length) return null;
  const running = jobs.filter(isRunning).length;
  return (
    <section className="lto-tray" aria-label={t('ltoTrayTitle')}>
      <div className="lto-tray-head">
        <b>
          {t('ltoTrayTitle')}
          {running > 0 && <span className="lto-count hot">{running}</span>}
        </b>
        {running > 0 && <small>{t('ltoJobStay')}</small>}
      </div>
      {jobs.map((j) => (
        <div key={j.key} className={`lto-job st-${j.stage}`}>
          <div className="lto-job-top">
            <b className="lto-job-title" title={j.title}>
              {j.title}
            </b>
            {isRunning(j) ? (
              <button type="button" className="lto-btn sm ghost" onClick={() => window.confirm(t('ltoJobCancelAsk')) && cancelPreview(j.key)}>
                {t('cancel')}
              </button>
            ) : (
              <button type="button" className="lto-pick-x" aria-label={t('close')} onClick={() => dismissPreview(j.key)}>
                ✕
              </button>
            )}
          </div>
          <div className="lto-job-file muted">
            {j.fileName} · {formatBytes(j.fileSize)} · {j.way === 'browser' ? t('ltoSrcBrowser') : t('ltoSrcServer')}
          </div>
          <JobProgress job={j} />
          {j.stage === 'done' && (
            <button type="button" className="lto-link lto-job-open" onClick={() => onOpen(j.videoId)}>
              {t('ltoJobOpen')} →
            </button>
          )}
          {j.error && <div className="lto-err">{j.error}</div>}
        </div>
      ))}
    </section>
  );
}
